import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import {assignIfDeepDifferent} from '../../../../../reducers/utils'
import type {PipelineId} from '../../../../../types'
import {truthy} from '../../../../../utils/guards'
import type {NewVcsRootDto} from '../../services/pipelinesApi.types'
import type {JobRepositorySettings} from '../../types'
import {getVcsRootReference} from '../EditPipelinePage.utils'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty, getJob} from './EditPipelinePage.slices.utils'

const mainRepositryName = 'main'

export const updateMainVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<Partial<NewVcsRootDto> & {pipelineId: PipelineId}>
> = (state, action) => {
  const {pipelineId, ...updateVcsRoot} = action.payload
  const {draft} = state[pipelineId] ?? {}

  if (draft != null) {
    draft.vcsRoot = {
      ...draft.vcsRoot,
      ...updateVcsRoot,
    }
  }
}

export const setAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<NewVcsRootDto & {pipelineId: PipelineId}>
> = (state, action) => {
  const {pipelineId, ...vcsRoot} = action.payload
  const {draft} = state[pipelineId] ?? {}
  if (draft != null) {
    draft.additionalVcsRoots ??= []
    draft.additionalVcsRoots?.push(vcsRoot)
  }
}

export const deleteAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; reference: string}>
> = (state, action) => {
  const mainRepositoryName = 'main'
  const {id, reference} = action.payload
  const pipelineState = state[id]
  const pipelineJobs = pipelineState?.draft?.settings.jobs ?? {}

  state[id] ??= {}
  state[id]!.deleted ??= {}
  state[id]!.deleted!.additionalVcsRoots ??= []
  state[id]!.deleted!.additionalVcsRoots!.push(reference)

  // Set enabled the main repository if not one remaining
  Object.entries(pipelineJobs).forEach(([_, value]) => {
    const jobRepositories = value.repositories ?? []
    const repositoriesKeysSet = new Set(
      jobRepositories.flatMap(repository => Object.keys(repository)),
    )
    const hasReferencedRepositories = repositoriesKeysSet.has(reference)
    const isMainRepositoryDisabled = repositoriesKeysSet.has(mainRepositoryName)
    const isMainEnabled =
      hasReferencedRepositories && isMainRepositoryDisabled && repositoriesKeysSet.size === 2

    if (isMainEnabled) {
      value.repositories = value.repositories?.filter(
        repository => !(mainRepositoryName in repository),
      )
    }
  })
}

export const deleteIncompleteAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; index: number}>
> = (state, action) => {
  const {id, index} = action.payload
  const additionalVcsRoots = state[id]?.draft?.additionalVcsRoots

  state[id] ??= {}
  state[id]!.deleted ??= {}
  state[id]!.deleted!.incompleteAdditionalVcsRoots ??= []
  state[id]!.deleted!.incompleteAdditionalVcsRoots!.push(index)

  if (additionalVcsRoots?.length != null) {
    state[id]!.draft!.additionalVcsRoots = additionalVcsRoots.filter(
      (_, itemIndex) => itemIndex !== index,
    )
  }
}

export const restoreAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; reference: string}>
> = (state, action) => {
  const {id, reference} = action.payload
  const {deleted} = state[id] ?? {}

  if (deleted?.additionalVcsRoots?.includes(reference)) {
    deleted.additionalVcsRoots = deleted.additionalVcsRoots.filter(
      deletedReference => deletedReference !== reference,
    )
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    deleteIfEmpty(deleted, 'additionalVcsRoots') && deleteIfEmpty(state[id], 'deleted')
  }
}

export const updateAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<Partial<NewVcsRootDto> & {pipelineId: PipelineId; index: number}>
> = (state, action) => {
  const {pipelineId, index, ...updateVcsRoot} = action.payload
  const {draft} = state[pipelineId] ?? {}

  if (draft != null) {
    const additionalVcsRoot = draft?.additionalVcsRoots[index]
    const jobs = draft?.settings.jobs
    const prevReference = getVcsRootReference(additionalVcsRoot)
    const updatedReference = getVcsRootReference({
      ...additionalVcsRoot,
      ...updateVcsRoot,
    })
    const prevUrl = additionalVcsRoot?.url
    const updatedUrl = updateVcsRoot.url ?? additionalVcsRoot?.url

    const referencesToReplace = new Set(
      [prevReference, prevUrl, updatedUrl]
        .filter(truthy)
        .filter(value => value !== updatedReference),
    )

    if (updatedReference != null && referencesToReplace.size > 0) {
      Object.keys(jobs ?? []).forEach(key => {
        const job = jobs![key]
        const repositories = job.repositories
        const jobRepository = repositories?.find(repository => {
          const [reference] = Object.keys(repository)
          return reference != null && referencesToReplace.has(reference)
        })

        if (jobRepository && repositories) {
          const [reference] = Object.keys(jobRepository)
          const hasUpdatedReference = repositories.some(
            repository => updatedReference in repository,
          )
          if (!hasUpdatedReference) {
            job.repositories?.push({
              [updatedReference]: jobRepository[reference],
            })
          }

          job.repositories = repositories?.filter(repository => {
            const [referenceKey] = Object.keys(repository)
            return referenceKey != null && !referencesToReplace.has(referenceKey)
          })
        }
      })
    }
    assignIfDeepDifferent(additionalVcsRoot, updateVcsRoot)
  }
}

export const updateJobRepository: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    url: string
    settings: JobRepositorySettings
  }>
> = (state, action) => {
  const {pipelineId, settings, jobId, url} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    const isMainRepositry = url === mainRepositryName
    const indexRepositry = job.repositories?.findIndex(repository => url in repository)
    const hasRepositry = indexRepositry !== undefined && indexRepositry !== -1

    job.repositories ??= []

    if (isMainRepositry && hasRepositry) {
      if (settings.enabled && !settings.path) {
        job.repositories = job.repositories?.filter(jobRepository => !(url in jobRepository))
      } else {
        job.repositories![indexRepositry] = {
          [url]: settings,
        }
      }
    } else if (isMainRepositry && !hasRepositry) {
      job.repositories?.push({
        [url]: settings,
      })
    } else if (!isMainRepositry && hasRepositry) {
      job.repositories = job.repositories?.reduce<Record<string, JobRepositorySettings>[]>(
        (accumulator, repository) => {
          if (url in repository) {
            repository[url] = settings

            const {enabled} = repository[url]

            if (enabled) {
              accumulator.push(repository)
            }
          } else {
            accumulator.push(repository)
          }

          return accumulator
        },
        [],
      )
    } else {
      job.repositories.push({[url]: settings})
    }

    if (!job.repositories?.length) {
      deleteIfEmpty(job, 'repositories')
    }
  }
}
